// Library imports
import React, { useState } from 'react';
import moment from 'moment';
import { MDBBtn, MDBIcon, MDBBadge, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter } from 'mdb-react-ui-kit';
import { bool, func, shape, string } from 'prop-types';
// Component imports
import Prediction from './windows/Prediction';
import Schedule from './windows/Schedule';
import InjuryList from './windows/InjuryList';
import PublicMoneyPercent from './windows/PublicMoneyPercent';
import LineData from './windows/LineData/index';
import PowerRank from './windows/PowerRank';
import KeyFactors from './windows/KeyFactors';
import Pitchers from './windows/Pitchers';
import Totals from './windows/Totals';
import Rosters from './windows/Rosters';
// Styled components
import {
    BadgeContainer,
    ContextBadge,
    ContextBadgeContainer,
    GameCardHeaderWrapper,
    GameCardSubtitle,
    GameCardTitle,
    GameCardTitleWrapper,
    HeaderActionsRow,
    UnlockButton,
    Toolbar,
    GameCardWrapper,
    ToolbarButton,
    GameCardContentWrapper,
    HeaderButtonContainer,
} from './styles';
// Util imports
import { gameMissingPrediction, getMissingPredictionMessage } from './utils';
// Constants
import { WINDOW_DATA } from './constants';

const GameCard = ({ demo = false, sport, game, defaultWindow = 'power-rank', vipPlay = false, history = false, hasPropsAccess = false, onUnlockPrediction = null }) => {
    // Local state
    const [window, setWindow] = useState(WINDOW_DATA.find(data => data.id === defaultWindow));
    const [showNoPredictionModal, setShowNoPredictionModal] = useState(false);

    const handleChangeWindow = e => {
        setWindow(WINDOW_DATA.find(data => data.id === e.currentTarget.id));
    };

    if (!game) return null;

    const isDivisionGame = game.homeTeam?.division && game.awayTeam?.division &&
        game.homeTeam.division === game.awayTeam.division;
    const isConferenceGame = !isDivisionGame && game.homeTeam?.conference && game.awayTeam?.conference &&
        game.homeTeam.conference === game.awayTeam.conference;
    const unlockMeta = game.predictionUnlock || null;
    const isLockedPrediction = Boolean(unlockMeta && !unlockMeta.unlocked);

    const missingPrediction = gameMissingPrediction(game);

    const renderUnlockButton = compact => {
        if (!isLockedPrediction || typeof onUnlockPrediction !== 'function') return null;
        const creditCost = Number(unlockMeta.creditCost || 1);
        // Not the native `disabled` attribute: the click still has to open the explanation modal.
        return (
            <UnlockButton
                size="sm"
                color="warning"
                $compact={compact}
                $unavailable={missingPrediction}
                aria-disabled={missingPrediction}
                onClick={() => (missingPrediction ? setShowNoPredictionModal(true) : onUnlockPrediction(game))}
            >
                <MDBIcon icon="unlock" style={{ marginRight: compact ? 5 : 7 }} />
                Unlock ({creditCost} Credit{creditCost > 1 ? 's' : ''})
            </UnlockButton>
        );
    };

    return (
        <GameCardWrapper key={game.gameId} id={game.gameId} data-tour="game-card">
            <GameCardHeaderWrapper>
                <GameCardTitleWrapper>
                    <GameCardTitle>{window.title}</GameCardTitle>
                    <GameCardSubtitle>{game.status === 'complete' ? game.status : moment(new Date(game.dateTime)).format('MM/DD hh:mm A')}</GameCardSubtitle>
                    <BadgeContainer>
                        {game.status === 'live' && (
                            <MDBBadge color="danger" className="me-1">
                                Live
                            </MDBBadge>
                        )}
                        {vipPlay && <MDBBadge color="warning">VIP Play</MDBBadge>}
                    </BadgeContainer>
                    {renderUnlockButton(true)}
                </GameCardTitleWrapper>
                <HeaderButtonContainer>
                    <HeaderActionsRow>
                        {renderUnlockButton(false)}
                        <ContextBadgeContainer>
                            {isDivisionGame && (
                                <ContextBadge $kind="division">
                                    <i className="fas fa-layer-group" />
                                    Division
                                </ContextBadge>
                            )}
                            {isConferenceGame && (
                                <ContextBadge $kind="conference">
                                    <i className="fas fa-users" />
                                    Conference
                                </ContextBadge>
                            )}
                        </ContextBadgeContainer>
                    </HeaderActionsRow>
                </HeaderButtonContainer>
            </GameCardHeaderWrapper>
            <GameCardContentWrapper>
                {window.id === 'power-rank' && <PowerRank demo={demo} game={game} sport={sport} history={history} />}
                {window.id === 'line-movement' && <LineData demo={demo} game={game} sport={sport} />}
                {window.id === 'percent' && <PublicMoneyPercent game={game} sport={sport} />}
                {window.id === 'prediction' && (
                    <Prediction
                        game={game}
                        sport={sport}
                        isLocked={isLockedPrediction}
                        unlockCredits={Number(unlockMeta?.creditCost || 1)}
                    />
                )}
                {window.id === 'injuries' && <InjuryList game={game} sport={sport} />}
                {window.id === 'schedule' && <Schedule game={game} sport={sport} demo={demo} />}
                {window.id === 'key-factor' && <KeyFactors game={game} sport={sport} />}
                {window.id === 'pitchers' && <Pitchers game={game} sport={sport} />}
                {window.id === 'totals' && (
                    <Totals
                        game={game}
                        sport={sport}
                        demo={demo}
                        isLocked={isLockedPrediction}
                        unlockCredits={Number(unlockMeta?.creditCost || 1)}
                    />
                )}
                {window.id === 'rosters' && <Rosters game={game} sport={sport} demo={demo} hasPropsAccess={hasPropsAccess} />}
            </GameCardContentWrapper>
            <Toolbar data-tour="game-toolbar">
                <ToolbarButton id="power-rank" onClick={handleChangeWindow} active={(window.id === 'power-rank').toString()}>
                    <MDBIcon icon="list-ol" />
                    <span>Power</span>
                </ToolbarButton>
                <ToolbarButton
                    disabled={demo}
                    id="line-movement"
                    onClick={handleChangeWindow}
                    active={(window.id === 'line-movement').toString()}
                >
                    <MDBIcon icon="chart-line" />
                    <span>Lines</span>
                </ToolbarButton>
                <ToolbarButton disabled={demo} id="percent" onClick={handleChangeWindow} active={(window.id === 'percent').toString()}>
                    <MDBIcon icon="percentage" />
                    <span>Public</span>
                </ToolbarButton>
                <ToolbarButton disabled={demo} id="key-factor" onClick={handleChangeWindow} active={(window.id === 'key-factor').toString()}>
                    <MDBIcon icon="key" />
                    <span>Factors</span>
                </ToolbarButton>
                {sport === 'mlb' && (
                    <ToolbarButton disabled={demo} id="pitchers" onClick={handleChangeWindow} active={(window.id === 'pitchers').toString()}>
                        <MDBIcon icon="baseball-ball" />
                        <span>Pitchers</span>
                    </ToolbarButton>
                )}
                <ToolbarButton disabled={demo} id="totals" onClick={handleChangeWindow} active={(window.id === 'totals').toString()}>
                    <MDBIcon icon="equals" />
                    <span>Totals</span>
                </ToolbarButton>
                <ToolbarButton disabled={demo} id="injuries" onClick={handleChangeWindow} active={(window.id === 'injuries').toString()}>
                    <MDBIcon icon="file-medical" />
                    <span>Injuries</span>
                </ToolbarButton>
                <ToolbarButton disabled={demo} id="rosters" onClick={handleChangeWindow} active={(window.id === 'rosters').toString()}>
                    <MDBIcon icon="users" />
                    <span>Rosters</span>
                </ToolbarButton>
                <ToolbarButton
                    disabled={demo || history ? true : false}
                    id="schedule"
                    onClick={handleChangeWindow}
                    active={(window.id === 'schedule').toString()}
                >
                    <MDBIcon icon="calendar" />
                    <span>Schedules</span>
                </ToolbarButton>
                <ToolbarButton id="prediction" onClick={handleChangeWindow} active={(window.id === 'prediction').toString()}>
                    <MDBIcon icon="calculator" />
                    <span>Prediction</span>
                </ToolbarButton>
            </Toolbar>
            <MDBModal open={showNoPredictionModal} onClose={() => setShowNoPredictionModal(false)} tabIndex="-1">
                <MDBModalDialog centered>
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>Prediction Not Available</MDBModalTitle>
                        </MDBModalHeader>
                        <MDBModalBody>{getMissingPredictionMessage(sport)}</MDBModalBody>
                        <MDBModalFooter>
                            <MDBBtn color="secondary" onClick={() => setShowNoPredictionModal(false)}>
                                Close
                            </MDBBtn>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </GameCardWrapper>
    );
};

GameCard.propTypes = {
    demo: bool,
    sport: string.isRequired,
    game: shape({}).isRequired,
    defaultWindow: string,
    vipPlay: bool,
    history: bool,
    hasPropsAccess: bool,
    onUnlockPrediction: func,
};

export default GameCard;
